import type { Flavor } from "@base/util/Brand"
import { IVec3 } from "./Vec3";

export type Degree = Flavor<number, "Degree">
export type Radian = Flavor<number, "Radian">

export const deg2rad: Radian = Math.PI / 180
export const rad2deg: Degree = 180 / Math.PI

export class MathUtil {
  static deg2rad(deg: Degree): Radian {
    return (deg * Math.PI) / 180.0;
  }

  static rad2deg(rad: Radian): Degree {
    return (rad * 180.0) / Math.PI;
  }

  static radPositive(rad: Radian): Radian {
    const r = rad % (2 * Math.PI);
    return r >= 0 ? r : r + Math.PI * 2
  }

  static clamp(n: number, min: number, max: number): number {
    if (n < min) return min;
    if (n > max) return max;
    return n;
  }

  static mod(x: number, n: number): number {
    return ((x % n) + n) % n;
  }

  static intDiv(a: number, b: number): number {
    const result = a / b;
    if (result >= 0) return Math.floor(result);
    else return Math.ceil(result);
  }

  static lerp(from: number, to: number, factor: number): number {
    return from * (1.0 - factor) + to * factor;
  }

  static round(num: number, decimals: number): number {
    if (!isNaN(num) && isFinite(num)) {
      const decimalPower = 10 ** decimals;
      return Math.round(num * decimalPower) / decimalPower;
    }
    return NaN;
  }

  static ceil(num: number, decimals: number): number {
    if (!isNaN(num) && isFinite(num)) {
      const decimalPower = 10 ** decimals;
      return Math.ceil(num * decimalPower) / decimalPower;
    }
    return NaN;
  }

  static floor(num: number, decimals: number): number {
    if (!isNaN(num) && isFinite(num)) {
      const decimalPower = 10 ** decimals;
      return Math.floor(num * decimalPower) / decimalPower;
    }
    return NaN;
  }

  static solveQuadratic(a: number, b: number, c: number): number[]; // ax^2 + bx + c = 0
  static solveQuadratic(p: number, q: number): number[]; // x^2 +px + q = 0
  static solveQuadratic(a: number, b: number, c?: number): number[] {
    if (c === undefined) {
      const p = a;
      const q = b;
      const inner = p ** 2 / 4 - q;
      if (inner < 0)
        return [];
      const term = Math.sqrt(inner);
      return [-p / 2 + term, -p / 2 - term]
    } else {
      const inner = b * b - 4 * a * c;
      if (inner < 0)
        return [];
      const term = Math.sqrt(inner);
      return [(-b + term) / (2 * a), (-b - term) / (2 * a)]
    }
  }

  static isNaN(vs: (number | IVec3 | undefined) | (number | IVec3)[]): boolean {
    if (!vs)
      return false;
    if (Array.isArray(vs)) {
      for (const v of vs) {
        if (typeof v === "number") {
          if (Number.isNaN(v))
            return true;
        } else {
          if (Number.isNaN(v.x) || Number.isNaN(v.y) || Number.isNaN(v.z))
            return true;
        }
      }
    } else {
      if (typeof vs === "number")
        return Number.isNaN(vs)
      else
        return Number.isNaN(vs.x) || Number.isNaN(vs.y) || Number.isNaN(vs.z);
    }
    return false;
  }

  static checkNaN<T extends (number | IVec3 | number[] | IVec3[] | undefined)>(vs: T, text?: string): T {
    if (MathUtil.isNaN(vs)) {
      throw new Error("isNan " + (text || ""))
    }
    return vs;
  }

  /**
   * Maps a value from an input range to an output range, clamping the result to the output range.
   * @param value The value to map.
   * @param inRangeA The start of the input range.
   * @param inRangeB The end of the input range.
   * @param outRangeA The start of the output range.
   * @param outRangeB The end of the output range.
   * @returns The mapped and clamped value.
   */
  static getMappedRangeValueClamped(
    value: number,
    inRangeA: number,
    inRangeB: number,
    outRangeA: number,
    outRangeB: number
  ): number {
    // Calculate the percentage of the value within the input range
    const rangePct = (value - inRangeA) / (inRangeB - inRangeA);
  
    // Clamp the percentage to the range [0, 1]
    const clampedPct = Math.max(0, Math.min(1, rangePct));
  
    // Map the clamped percentage to the output range
    return outRangeA + clampedPct * (outRangeB - outRangeA);
  }
}